/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import {
  hasCompletionDialogBeenDismissed,
  setLastDismissedCompletionDialogProjectId,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  projectTitle: string;
  projectId: string;
  isAutoPublished: boolean;
  onAddProjectClick: () => void;
  onEditProjectClick: () => void;
  onViewProjectClick: () => void;
};

const styles = {
  buttons: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
};

const ProjectCompletionDialog = ({
  isAutoPublished,
  projectTitle,
  projectId,
  onAddProjectClick,
  onEditProjectClick,
  onViewProjectClick,
}: PropsFromCaller) => {
  const track = useTracker();
  const initialIsOpen = !hasCompletionDialogBeenDismissed(projectId);
  const [open, setOpen] = React.useState(initialIsOpen);

  const handleClose = () => {
    setLastDismissedCompletionDialogProjectId(projectId);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Dialog.HeadingGroup>
        {isAutoPublished
          ? _t('Your hard work is paying off!')
          : _t('Your recently completed project would look great on your profile')}
      </Dialog.HeadingGroup>
      <Dialog.Content css={styles.body}>
        <Typography2 component="p" variant="bodyPrimary">
          {isAutoPublished ? (
            <FormattedHTMLMessage
              message={_t(
                "<strong>{projectTitle}</strong> has been added to your profile. Employers find these types of projects invaluable to understand the skills you've developed during your learning."
              )}
              projectTitle={projectTitle}
            />
          ) : (
            <FormattedHTMLMessage
              message={_t(
                "Nice work completing <strong>{projectTitle}</strong>! Employers find these types of projects invaluable to understand the skills you've developed during your learning."
              )}
              projectTitle={projectTitle}
            />
          )}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary">
          {isAutoPublished
            ? _t(
                'To stand out even more, add your own details to the completed project to illustrate your critical thinking and communication skills.'
              )
            : _t(
                'Feature this project on your profile by adding details to illustrate your critical thinking and communication skills.'
              )}
        </Typography2>
      </Dialog.Content>
      <Dialog.Actions>
        {isAutoPublished ? (
          <div css={styles.buttons}>
            <Button
              onClick={() => {
                track('click_button', {
                  button: { name: EVENTS.portfolioCompletedProjectDialogEdit as any },
                });
                onEditProjectClick();
                handleClose();
              }}
            >
              {_t('Edit project details')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                track('click_button', {
                  button: { name: EVENTS.portfolioCompletedProjectDialogView as any },
                });
                onViewProjectClick();
                handleClose();
              }}
            >
              {_t('View project')}
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => {
              track('click_button', {
                button: { name: EVENTS.portfolioCompletedProjectDialogAdd as any },
              });
              onAddProjectClick();
              handleClose();
            }}
          >
            {_t('Add project to profile')}
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
};

export default ProjectCompletionDialog;
